import { useEffect, useMemo } from 'react'
import { ActivityIndicator } from 'react-native'

import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigation } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import ScreenViewEventWrapper from '@zet/common/components/screenViewEventWrapper'
import { zetProFixedList } from '@zet/common/utils/zetProData'
import { useAddCustomerNextStateWeb } from '@zet/customer/src/api/hooks'
import {
  useAgentDetailsWF,
  useGetAgentBanners,
} from '@zet/customer/src/api/hooks/webFlow'
import AddCustomerHeader from '@zet/customer/src/components/addCustomerHeader'
import CustomerInterest from '@zet/customer/src/components/customerInterest'
import useMyCustomersStore from '@zet/customer/src/store/myCustomers'
import { colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Button } from '@zet/design-system/button'
import { Input, InputGroup } from '@zet/design-system/input'
import { Stack } from '@zet/design-system/layout'
import { B1, B2, B5 } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import { getLoginHash } from '@zet/utils/loginHash'
import { MOBILE_NUMBER_REGEX, NAME_REGEX } from '@zet/utils/regularExpression'
import {
  GENERIC_ALPHABET_FIELD_VALIDATION,
  MOBILE_NUMBER_VALIDATION,
  NAME_FIELD_EMPTY,
  NAME_FIELD_VALIDATION,
} from '@zet/utils/validationMessage'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import KeyBoardWrapper from '@components/keyboardWrapper'

import CustomImageHeader from '../../../components/bannerHeader'
import {
  getTrackEventForClickAction,
  getTrackEventForScreenView,
} from './utils'

const NameAndPhoneNumber = () => {
  const { mutate: mutateWithExperiments, isLoading: isContinueLoading } =
    useAddCustomerNextStateWeb()
  const { customerIntent } = useMyCustomersStore((state) => state)
  const { data: agentData, isLoading } = useAgentDetailsWF()
  const { data: bannerData, isLoading: isBannerLoading } = useGetAgentBanners({
    onecode: agentData?.onecode,
  })
  const isCreditScoreCheckFlow =
    window.location.pathname === '/customer/check-credit-score'
  const isReconsentFlow = window.location.pathname.includes(
    'bureauReportConsent',
  )

  const isZetPro = useMemo(() => {
    return zetProFixedList.some(
      (id) => Number(id) === Number(agentData?.onecodeId),
    )
  }, [agentData?.onecodeId])

  const validationSchema = useMemo(() => {
    return z.object({
      fullName: z
        .string()
        .trim()
        .min(1, { message: NAME_FIELD_EMPTY })
        .regex(new RegExp(NAME_REGEX), NAME_FIELD_VALIDATION),
      mobileNumber: z
        .string()
        .min(1, { message: NAME_FIELD_EMPTY })
        .regex(new RegExp(MOBILE_NUMBER_REGEX), MOBILE_NUMBER_VALIDATION)
        .max(10, { message: MOBILE_NUMBER_VALIDATION }),
      advisorCode: z
        .string()
        .regex(
          new RegExp('^[A-Za-z0-9]{2,254}$'),
          GENERIC_ALPHABET_FIELD_VALIDATION,
        )
        .optional(),
      email: isZetPro
        ? z.string().email({ message: 'Invalid email' }).optional()
        : z.string().email({ message: 'Invalid email' }),
    })
  }, [isZetPro])

  useEffect(() => {
    if (isCreditScoreCheckFlow) {
      sessionStorage.setItem('isCreditScoreCheck', 'true')
    } else {
      sessionStorage.removeItem('isCreditScoreCheck')
    }
  }, [])
  const navigation = useNavigation()

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<z.infer<typeof validationSchema>>({
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    if (agentData?.onecodeId && !isCreditScoreCheckFlow) {
      const payload = getTrackEventForScreenView(agentData)
      publishEvent('screen_view', payload)
    }
  }, [agentData?.onecodeId])

  useEffect(() => {
    navigation.setOptions({
      header: () => (!bannerData?.imageUrl ? <AddCustomerHeader /> : <></>),
    })
  }, [bannerData?.imageUrl])

  useEffect(() => {
    if (agentData && isReconsentFlow) {
      setDefaultValues()
    }
  }, [agentData])

  const setDefaultValues = () => {
    setValue('fullName', agentData?.customer?.fullName || '')
    setValue('mobileNumber', agentData?.customer?.phoneNumber?.toString() || '')
    setValue('email', agentData?.customer?.email || '')
    trigger()
  }

  const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'number') {
        return { message: NAME_FIELD_EMPTY }
      }
    }

    if (issue.code === z.ZodIssueCode.invalid_type) {
      if (issue.expected === 'string') {
        return { message: NAME_FIELD_EMPTY }
      }
    }

    return { message: ctx.defaultError }
  }
  z.setErrorMap(customErrorMap)

  const onSubmit = () => {
    const fullName = getValues('fullName').trim()
    const phoneNumber = getValues('mobileNumber') as string
    const email = isZetPro ? null : getValues('email')?.trim()

    if (agentData?.onecodeId) {
      const payload = getTrackEventForClickAction(agentData, {
        fullName,
        phoneNumber,
        customerIntent,
        ...(email ? { email } : {}),
      })

      publishEvent('click_action', payload)
    }

    const pathName = window?.location?.pathname
    const requestBodyData = {
      fullName,
      phoneNumber,
      ...(email ? { email } : {}),
      hash: encodeURIComponent(getLoginHash() || ''),
      customerIntent,
      categoryId: agentData?.categoryId,
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      isReconsentFlow: pathName.includes('bureauReportConsent'),
      isCreditCheck: isCreditScoreCheckFlow,
      customerAdditionSource: agentData?.customerAdditionSource,
      ...(agentData?.isDsa
        ? {
            meta: {
              advisorCode: getValues('advisorCode')?.trim(),
            },
          }
        : {}),
    }
    mutateWithExperiments(requestBodyData)
  }

  useEffect(() => {
    if (agentData?.isShortendCustomerJourney) {
      const customerLoginPathName = ['/customer/login']
      if (customerLoginPathName.includes(window?.location?.pathname)) {
        window.location.replace(
          `${window.location.origin}/customer/product${window?.location?.search}`,
        )
      }
    }
  }, [agentData?.isShortendCustomerJourney])

  if (isLoading || isBannerLoading || !agentData) {
    return (
      <Stack className="flex-1 items-center justify-center bg-white">
        <ActivityIndicator size={'large'} color={'blue'} />
      </Stack>
    )
  }

  return (
    <ScreenViewEventWrapper screenName="add_customer_name_phonenumber_page">
      <KeyBoardWrapper
        className="flex-1 bg-white "
        BottomSheet={
          <Stack className="border-grays-30 border-t py-2">
            <Button
              className="mx-3"
              onPress={handleSubmit(onSubmit)}
              disabled={!isValid || (!agentData?.partnerId && !customerIntent)}
              style={{
                ...((!isValid ||
                  (!agentData?.partnerId && !customerIntent)) && {
                  backgroundColor: zetColors.grays[50],
                }),
              }}
              loading={isContinueLoading}
              size="lg"
              eventName="click_action"
              eventPayload={{
                item_name: 'add_customer_step_0_continue',
                customer_name: getValues()?.fullName,
                customer_phone: getValues()?.mobileNumber,
                customer_email: getValues()?.email,
                url: window?.location?.href,
              }}
            >
              {i18n.t('bottomView.continue')}
            </Button>
          </Stack>
        }
      >
        {bannerData?.imageUrl && (
          <CustomImageHeader bannerUrl={bannerData?.imageUrl} />
        )}
        <Stack>
          <Stack
            className={`${
              bannerData?.imageUrl &&
              '-mt-3 rounded-tl-xl rounded-tr-xl bg-white '
            } px-3`}
          >
            <Stack className="mb-1 mt-3">
              <B2 className="text-grays-80">
                {i18n.t('webFlow.nameAndPhoneNumber.yourName')}
                <B1 className="text-error-100 ml-1">*</B1>
              </B2>
              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Input
                    placeholder="Eg: Ankush Singh"
                    onBlur={(e) => onBlur(e)}
                    onChangeText={(value) => {
                      onChange(value)
                    }}
                    size="md"
                    value={value}
                    type={`${
                      errors.fullName?.message
                        ? 'error'
                        : value && errors.fullName?.message
                          ? 'primary'
                          : !value
                            ? 'primary'
                            : 'success'
                    }`}
                    showErrorText={errors.fullName?.message}
                    className="mt-1 "
                    editable={!isReconsentFlow}
                  />
                )}
                name="fullName"
              />
            </Stack>
            <Stack className="mt-3">
              <B2 className="text-grays-80 mb-1">
                {i18n.t('webFlow.nameAndPhoneNumber.yourPhoneNumber')}
                <B1 className="text-error-100 ml-1">*</B1>
              </B2>

              <Controller
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <InputGroup
                    placeholder="900 000 0000"
                    onBlur={(e) => onBlur(e)}
                    onChangeText={(value) => onChange(value)}
                    value={value}
                    type={`${
                      errors.mobileNumber?.message
                        ? 'error'
                        : value && errors.mobileNumber?.message
                          ? 'primary'
                          : !value
                            ? 'primary'
                            : 'success'
                    }`}
                    size="lg"
                    maxLength={10}
                    keyboardType="number-pad"
                    editable={!isReconsentFlow}
                  />
                )}
                name="mobileNumber"
              />
              {errors.mobileNumber?.message ? (
                <Stack className="mt-2">
                  <B5 className="text-error-100">
                    {errors.mobileNumber?.message}
                  </B5>
                </Stack>
              ) : null}
            </Stack>

            {/* Email */}
            {isZetPro ? null : (
              <Stack className="mt-3">
                <B2 className="text-grays-80 mb-1">
                  {i18n.t('webFlow.basicDetails.email')}
                  <B1 className="text-error-100 ml-1">*</B1>
                </B2>

                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder="Eg: username@gmail.com"
                      onBlur={onBlur}
                      onChangeText={(value) => {
                        onChange(value)
                      }}
                      size="md"
                      value={value}
                      type={`${errors.email?.message ? 'error' : 'primary'}`}
                    />
                  )}
                  name="email"
                />
                {errors?.email?.message ? (
                  <Stack className="mt-2">
                    <B5 className="text-error-100">{errors?.email?.message}</B5>
                  </Stack>
                ) : null}
              </Stack>
            )}

            {agentData?.isDsa && (
              <Stack className="mt-3 ">
                <B2 className="text-grays-80 mb-1">
                  {i18n.t('webFlow.nameAndPhoneNumber.advisorCode')}
                </B2>
                <Controller
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Input
                      placeholder="001"
                      onBlur={(e) => {
                        onBlur(e)
                      }}
                      size="md"
                      onChangeText={(value) => {
                        onChange(value)
                      }}
                      value={value}
                      type={`${
                        errors.advisorCode?.message
                          ? 'error'
                          : value && errors.advisorCode?.message
                            ? 'primary'
                            : !value
                              ? 'primary'
                              : 'success'
                      }`}
                      showErrorText={errors.advisorCode?.message}
                      className="mt-1 "
                    />
                  )}
                  name="advisorCode"
                />
              </Stack>
            )}
            {agentData?.partnerId !== 193 && (
              <Stack className="mt-3">
                <Alert
                  intent="success"
                  iconLeft={{
                    name: 'SecureOutlined',
                    color: zetColors.success[150],
                  }}
                >
                  {i18n.t('webFlow.nameAndPhoneNumber.weWillSend')}
                </Alert>
              </Stack>
            )}
            {Boolean(!agentData?.partnerId) && <CustomerInterest />}
          </Stack>
        </Stack>
      </KeyBoardWrapper>
    </ScreenViewEventWrapper>
  )
}

export default NameAndPhoneNumber
